import React, { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import styled, { keyframes } from 'styled-components'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import TimelapseRoundedIcon from '@mui/icons-material/TimelapseRounded'
import PercentRoundedIcon from '@mui/icons-material/PercentRounded'
import { getTaskAnalytics } from 'services/taskService'
import KpiReportButton from './KpiReportButton'
import PerformanceScopePicker from './PerformanceScopePicker'
import { PRIVATE_PATHS } from 'utils/paths'
import ReportPeriodPicker, { resolvePeriod } from 'components/ReportPeriodPicker'
import TaskOriginPicker from './TaskOriginPicker'
import { TASK_ORIGINS, originLabel } from 'features/taskManagement/taskOrigin'
import CountDonut from 'features/dashboard/charts/CountDonut'
import MonthlyStackedBars from 'features/dashboard/charts/MonthlyStackedBars'
import HorizontalStackedBars from 'features/dashboard/charts/HorizontalStackedBars'
import { TASK_PROGRESS_BUCKETS, bucketsFromRow, bucketTotal, taskBucketsWithWindow } from 'features/taskManagement/taskProgressBuckets'
import ChartKeyExplainer from 'features/dashboard/charts/ChartKeyExplainer'

const RAG = { green: '#15803d', amber: '#d97706', red: '#b00020', neutral: '#334155' }

const fmtDuration = h => {
  if (h == null) return '-'
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`
  if (h < 48) return `${Math.round(h)}h`
  return `${Math.round(h / 24)}d`
}

const rateColour = rate => (rate == null ? undefined : rate >= 80 ? RAG.green : rate >= 50 ? RAG.amber : RAG.red)

const PERSON_COLUMNS = [
  { key: 'open', label: 'Open', title: 'Tasks not finished yet', drill: 'open=1' },
  { key: 'upcoming', label: 'Upcoming', title: 'Not finished yet, and not due for a while', drill: 'open=1' },
  { key: 'dueSoon', label: 'Due soon', title: 'Not finished yet, and due within the next few days', drill: 'open=1', colour: v => (v ? RAG.amber : undefined) },
  { key: 'completed', label: 'Done', title: 'Tasks finished', drill: 'status=completed', colour: () => RAG.green },
  { key: 'completedOnTime', label: 'On time', title: 'Finished on or before the due date', drill: 'status=completed', colour: () => RAG.green, mobile: true },
  { key: 'completedLate', label: 'Late', title: 'Finished after the due date', drill: 'completed_late=1', colour: v => (v ? RAG.amber : undefined), mobile: true },
  { key: 'overdue', label: 'Overdue', title: 'Not finished and past the due date', drill: 'overdue=1', colour: v => (v ? RAG.red : undefined), mobile: true },
  { key: 'onTimeRate', label: 'On-time %', title: 'Share of finished tasks that were on time', drill: 'status=completed', colour: rateColour, percent: true },
]

const SITE_COLUMNS = [
  { key: 'total', label: 'Tasks', title: 'All tasks for this site in the period' },
  { key: 'upcoming', label: 'Upcoming', title: 'Not finished yet, and not due for a while', drill: 'open=1' },
  { key: 'dueSoon', label: 'Due soon', title: 'Not finished yet, and due within the next few days', drill: 'open=1', colour: v => (v ? RAG.amber : undefined) },
  { key: 'completed', label: 'Done', title: 'Tasks finished', drill: 'status=completed', colour: () => RAG.green },
  { key: 'completedOnTime', label: 'On time', title: 'Finished on or before the due date', drill: 'status=completed', colour: () => RAG.green, mobile: true },
  { key: 'completedLate', label: 'Late', title: 'Finished after the due date', drill: 'completed_late=1', colour: v => (v ? RAG.amber : undefined), mobile: true },
  { key: 'overdue', label: 'Overdue', title: 'Not finished and past the due date', drill: 'overdue=1', colour: v => (v ? RAG.red : undefined), mobile: true },
  { key: 'onTimeRate', label: 'On-time %', title: 'Share of finished tasks that were on time', drill: 'status=completed', colour: rateColour, percent: true },
]

const useSortedRows = (rows, search, defaultKey) => {
  const [sort, setSort] = useState({ key: defaultKey, dir: 'desc' })
  const sorted = useMemo(() => {
    let list = rows || []
    const q = search.trim().toLowerCase()
    if (q) list = list.filter(r => (r.name || '').toLowerCase().includes(q))
    const dir = sort.dir === 'asc' ? 1 : -1
    return [...list].sort((a, b) => {
      if (sort.key === 'name') return (a.name || '').localeCompare(b.name || '') * dir
      const av = a[sort.key] == null ? -1 : a[sort.key]
      const bv = b[sort.key] == null ? -1 : b[sort.key]
      return (av - bv) * dir
    })
  }, [rows, search, sort])
  const toggle = key => setSort(s => (s.key === key
    ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: key === 'name' ? 'asc' : 'desc' }))
  const arrow = key => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '')
  return { sorted, toggle, arrow }
}

const TaskAnalyticsPanel = ({ onNavigate, embedded = false, scope: scopeProp, onScopeChange, period: periodProp, onPeriodChange }) => {
  const [scopeState, setScopeState] = useState({ type: 'all', id: null, label: 'All Building Passports' })
  const [periodState, setPeriodState] = useState({ preset: 'last_30_days' })
  const scope = scopeProp || scopeState
  const setScope = onScopeChange || setScopeState
  const period = periodProp || periodState
  const setPeriod = onPeriodChange || setPeriodState
  const [origin, setOrigin] = useState(null)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [personSearch, setPersonSearch] = useState('')
  const [siteSearch, setSiteSearch] = useState('')

  const range = useMemo(() => resolvePeriod(period), [period])

  const scopeParams = useMemo(() => {
    if (scope.type === 'portfolio') return { portfolioId: scope.id }
    if (scope.type === 'estate') return { estateId: scope.id }
    if (scope.type === 'bp') return { buildingPassportId: scope.id }
    return {}
  }, [scope])

  const filterParams = useMemo(() => ({
    ...(range.from ? { from: range.from } : {}),
    ...(range.to ? { to: range.to } : {}),
    ...(origin ? { origin } : {}),
  }), [range, origin])

  const load = useCallback(() => {
    setLoading(true)
    setError(false)
    getTaskAnalytics({ ...scopeParams, ...filterParams })
      .then(r => {
        setData(r?.data?.data)
        setLoading(false)
      })
      .catch(() => {
        setData(null)
        setError(true)
        setLoading(false)
      })
  }, [scopeParams, filterParams])

  useEffect(() => { load() }, [load])

  const scopePart = useMemo(() => {
    if (scope.type === 'portfolio') return `portfolio=${scope.id}`
    if (scope.type === 'estate') return `estate=${scope.id}`
    if (scope.type === 'bp') return `bp=${scope.id}`
    return ''
  }, [scope])

  const drill = (extra, overrides = {}) => {
    if (!onNavigate) return
    const chosenOrigin = overrides.origin !== undefined ? overrides.origin : origin
    const parts = [
      'tab=tasks',
      overrides.scope !== undefined ? overrides.scope : scopePart,
      chosenOrigin ? `origin=${chosenOrigin}` : '',
      range.from ? `deadline_from=${range.from}` : '',
      range.to ? `deadline_to=${range.to}` : '',
      extra,
    ].filter(Boolean)
    onNavigate(`${PRIVATE_PATHS.taskManagement}?${parts.join('&')}`)
  }

  const totals = data?.totals
  const domains = data?.byDomain || []
  const byOrigin = data?.byOrigin || []
  const people = useSortedRows(data?.byPerson, personSearch, 'completed')
  const sites = useSortedRows(data?.byBuilding, siteSearch, 'total')
  const clickable = !!onNavigate
  const dueSoonDays = data?.dueSoonDays ?? 7
  const windowedBuckets = useMemo(() => taskBucketsWithWindow(dueSoonDays), [dueSoonDays])
  const drillHint = clickable ? 'Click any colour to open that list of tasks.' : null

  const DRILL_BY_BUCKET = {
    upcoming: 'open=1',
    dueSoon: 'open=1',
    overdue: 'overdue=1',
    onHold: 'status=on_hold',
    completedLate: 'completed_late=1',
    completedOnTime: 'status=completed',
  }
  const distributionSegments = useMemo(
    () => (data?.distribution || [])
      .filter(d => Number(d.count) > 0)
      .map(d => {
        const camel = String(d.key).replace(/_(.)/g, (_, c) => c.toUpperCase())
        const meta = TASK_PROGRESS_BUCKETS.find(b => b.key === camel)
        return {
          key: d.key,
          label: meta?.label || d.label,
          tint: meta?.tint || d.colour,
          help: meta?.help || '',
          count: Number(d.count),
          drill: DRILL_BY_BUCKET[meta?.key],
        }
      }),
    [data],
  )

  const initialLoading = loading && !data && !error
  const niceDate = value => moment(value, 'YYYY-MM-DD').format('D MMM YYYY')
  const periodText = range.from
    ? `Counting tasks due between ${niceDate(range.from)} and ${niceDate(range.to)}.`
    : 'Counting all tasks, whenever they were due.'

  return (
    <Panel $embedded={embedded}>
      <Head>
        <div>
          {!embedded && <Title>Task performance</Title>}
          <Subtitle>How many tasks were finished, finished on time, finished late or are overdue. Choose the sites, the dates and the type of task to look at.</Subtitle>
        </div>
        <HeadActions>
          <KpiReportButton initial={{ scope, period, origin }} title='Download a KPI report with the sites, dates and task type shown here' />
          <PerformanceScopePicker scope={scope} onChange={setScope} />
        </HeadActions>
      </Head>

      <FilterRow>
        <ReportPeriodPicker value={period} onChange={setPeriod} label='Tasks due' />
        <TaskOriginPicker value={origin} onChange={setOrigin} />
      </FilterRow>
      <PeriodNote>{periodText} {origin ? `Showing ${originLabel(origin).toLowerCase()} tasks only.` : 'Showing all task types.'}</PeriodNote>

      {initialLoading ? (
        <>
          <Tiles>{Array.from({ length: 6 }).map((_, i) => <TileSkel key={i} />)}</Tiles>
          <SkelRows>{Array.from({ length: 4 }).map((_, i) => <SkelRow key={i} />)}</SkelRows>
        </>
      ) : error ? (
        <ErrorState>
          <span>Couldn’t load task performance.</span>
          <RetryBtn type='button' onClick={load}>Try again</RetryBtn>
        </ErrorState>
      ) : (
        <>
          <Tiles $dim={loading}>
            <Tile $clickable={clickable} onClick={() => drill('open=1')} title='Not finished yet, and not due for a while'><TileIcon $c={RAG.neutral}><ScheduleRoundedIcon style={{ fontSize: 18 }} /></TileIcon><TileVal>{totals?.upcoming ?? 0}</TileVal><TileLbl>Upcoming</TileLbl></Tile>
            <Tile $clickable={clickable} onClick={() => drill('open=1')} title={`Not finished yet, and due within the next ${data?.dueSoonDays ?? 7} days`}><TileIcon $c={RAG.amber}><ScheduleRoundedIcon style={{ fontSize: 18 }} /></TileIcon><TileVal $c={RAG.amber}>{totals?.dueSoon ?? 0}</TileVal><TileLbl>Due soon</TileLbl></Tile>
            <Tile $clickable={clickable} onClick={() => drill('status=completed')} title='View completed tasks'><TileIcon $c={RAG.green}><CheckCircleRoundedIcon style={{ fontSize: 18 }} /></TileIcon><TileVal>{totals?.completedOnTime ?? 0}</TileVal><TileLbl>Completed on time</TileLbl></Tile>
            <Tile $clickable={clickable} onClick={() => drill('completed_late=1')} title='View tasks completed late'><TileIcon $c={RAG.amber}><TimelapseRoundedIcon style={{ fontSize: 18 }} /></TileIcon><TileVal $c={RAG.amber}>{totals?.completedLate ?? 0}</TileVal><TileLbl>Completed late</TileLbl></Tile>
            <Tile $clickable={clickable} onClick={() => drill('overdue=1')} title='View overdue tasks'><TileIcon $c={RAG.red}><WarningAmberRoundedIcon style={{ fontSize: 18 }} /></TileIcon><TileVal $c={RAG.red}>{totals?.overdue ?? 0}</TileVal><TileLbl>Overdue</TileLbl></Tile>
            <Tile $clickable={clickable} onClick={() => drill('status=completed')} title='Share of finished tasks (with a due date) that were finished on time'><TileIcon $c={RAG.green}><PercentRoundedIcon style={{ fontSize: 18 }} /></TileIcon><TileVal $c={rateColour(totals?.onTimeRate)}>{totals?.onTimeRate != null ? `${totals.onTimeRate}%` : '-'}</TileVal><TileLbl>On-time rate</TileLbl></Tile>
            <Tile $clickable={clickable} onClick={() => drill('status=completed')} title='Average time from a task being created to it being finished. Tasks brought across from the previous system are left out, because it did not record when they were created.'><TileIcon $c={RAG.neutral}><TimelapseRoundedIcon style={{ fontSize: 18 }} /></TileIcon><TileVal>{fmtDuration(totals?.avgCompletionHours)}</TileVal><TileLbl>Avg time to finish</TileLbl></Tile>
          </Tiles>

          <Block $dim={loading} $wide>
            <BlockHead>
              <BlockTitle>
                Where the work stands
                <ChartKeyExplainer
                  title='Where the work stands'
                  lead='Every task due in the period you chose, counted once. The ring shows how they split.'
                  buckets={windowedBuckets}
                  drillNote={drillHint}
                />
              </BlockTitle>
              <BlockNote>Every task in this period, counted once.{clickable ? ' Click any colour to see those tasks.' : ''}</BlockNote>
            </BlockHead>
            <CountDonut
              segments={distributionSegments}
              centreLabel='Tasks'
              onSegmentClick={clickable ? seg => drill(seg.drill) : undefined}
            />
          </Block>

          <Block $dim={loading} $wide>
            <BlockHead>
              <BlockTitle>
                Month by month
                <ChartKeyExplainer
                  title='Month by month'
                  lead='Each column is one month, by task deadline. Past months show what was done, future months show what is still to come.'
                  buckets={windowedBuckets}
                />
              </BlockTitle>
              <BlockNote>What has been done, and what is still to come. This spans the months either side of today, whatever period you picked above.</BlockNote>
            </BlockHead>
            <MonthlyStackedBars months={data?.byMonth || []} buckets={windowedBuckets} />
          </Block>

          <OriginCards $dim={loading}>
            {TASK_ORIGINS.map(o => {
              const row = byOrigin.find(r => r.origin === o.value) || {}
              const active = origin === o.value
              return (
                <OriginCard
                  key={o.value}
                  type='button'
                  $color={o.color}
                  $active={active}
                  aria-pressed={active}
                  title={active ? 'Show all task types again' : `Show only ${o.label.toLowerCase()} tasks`}
                  onClick={() => setOrigin(active ? null : o.value)}
                >
                  <OriginName><Dot $c={o.color} />{o.label}</OriginName>
                  <OriginDesc>{o.description}</OriginDesc>
                  <OriginStats>
                    <span><strong>{row.total ?? 0}</strong> tasks</span>
                    {row.onTimeRate != null
                      ? <span style={{ color: rateColour(row.onTimeRate) }}><strong>{row.onTimeRate}%</strong> on time</span>
                      : <span>none finished yet</span>}
                    {row.overdue > 0 && <span style={{ color: RAG.red }}><strong>{row.overdue}</strong> overdue</span>}
                  </OriginStats>
                </OriginCard>
              )
            })}
          </OriginCards>

          <Block $dim={loading} $wide>
            <BlockHead>
              <BlockTitle>
                By site ({sites.sorted.length})
                <ChartKeyExplainer
                  title='By site'
                  lead='One bar per building, busiest first. The bar shows how that building’s tasks split.'
                  buckets={windowedBuckets}
                  drillNote={clickable ? 'Click a bar to open that building’s tasks.' : null}
                />
              </BlockTitle>
              <SearchBox>
                <SearchRoundedIcon style={{ fontSize: 15 }} />
                <input value={siteSearch} onChange={e => setSiteSearch(e.target.value)} placeholder='Search sites…' aria-label='Search sites' />
              </SearchBox>
            </BlockHead>
            {sites.sorted.length > 0 && (
              <>
                <HorizontalStackedBars
                  rows={sites.sorted}
                  buckets={windowedBuckets}
                  onRowClick={clickable ? r => drill('', { scope: `bp=${r.bpId}` }) : undefined}
                />
                <ChartTableRule />
              </>
            )}
            <StatTable
              rows={sites.sorted}
              columns={SITE_COLUMNS}
              nameLabel='Site'
              rowKey={r => r.bpId}
              sort={sites}
              clickable={clickable}
              emptyText={siteSearch ? 'No sites match your search.' : 'No tasks were due at any site in this period.'}
              onRow={(r, col) => drill(col?.drill || '', { scope: `bp=${r.bpId}` })}
            />
          </Block>

          <Grids $dim={loading}>
            <Block>
              <BlockHead>
                <BlockTitle>
                  By person ({people.sorted.length})
                  <ChartKeyExplainer
                    title='By person'
                    lead='One bar per person, busiest first. The bar shows how their tasks split.'
                    buckets={windowedBuckets}
                    drillNote={clickable ? 'Click a bar to open that person’s tasks.' : null}
                  />
                </BlockTitle>
                <SearchBox>
                  <SearchRoundedIcon style={{ fontSize: 15 }} />
                  <input value={personSearch} onChange={e => setPersonSearch(e.target.value)} placeholder='Search people…' aria-label='Search people' />
                </SearchBox>
              </BlockHead>
              {people.sorted.length > 0 && (
                <>
                  <HorizontalStackedBars
                    rows={people.sorted}
                    buckets={windowedBuckets}
                    limit={10}
                    onRowClick={clickable ? r => drill(`assignee=${r.userId}`) : undefined}
                  />
                  <ChartTableRule />
                </>
              )}
              <StatTable
                rows={people.sorted}
                columns={PERSON_COLUMNS}
                nameLabel='Person'
                rowKey={r => r.userId}
                sort={people}
                clickable={clickable}
                emptyText={personSearch ? 'No people match your search.' : 'No one has tasks assigned in this period.'}
                onRow={(r, col) => drill([`assignee=${r.userId}`, col?.drill].filter(Boolean).join('&'))}
              />
            </Block>

            <Block>
              <BlockHead><BlockTitle>By domain</BlockTitle></BlockHead>
              {domains.length === 0 ? (
                <Empty>No tasks in this period.</Empty>
              ) : (
                <DomainList>
                  {domains.map(d => {
                    const dp = d.domainId ? `domain=${d.domainId}` : ''
                    return (
                      <DomainRow key={d.domainId || 'none'}>
                        <DomainName $clickable={clickable} onClick={() => drill(dp)} title={`${d.name}: view its tasks`}><Dot $c={d.colour || '#94a3b8'} /><span>{d.name}</span></DomainName>
                        <DomainStats>
                          <DStat $clickable={clickable} onClick={() => drill(dp)} title='All tasks in this domain'>{d.total} total</DStat>
                          <DStat $c={RAG.green} $clickable={clickable} onClick={() => drill([dp, 'status=completed'].filter(Boolean).join('&'))} title='Completed'>{d.completed} done</DStat>
                          {d.overdue > 0 && <DStat $c={RAG.red} $clickable={clickable} onClick={() => drill([dp, 'overdue=1'].filter(Boolean).join('&'))} title='Overdue'>{d.overdue} overdue</DStat>}
                          {d.completedLate > 0 && <DStat $c={RAG.amber} $clickable={clickable} onClick={() => drill([dp, 'completed_late=1'].filter(Boolean).join('&'))} title='Completed late'>{d.completedLate} late</DStat>}
                        </DomainStats>
                      </DomainRow>
                    )
                  })}
                </DomainList>
              )}
            </Block>
          </Grids>
          <Legend>
            <strong>How these are worked out:</strong> a task counts as <em>on time</em> when it was finished on or before its due date, and <em>late</em> when it was finished after it. <em>Overdue</em> tasks are not finished and their due date has passed. The on-time rate is on-time tasks divided by all finished tasks that had a due date.
          </Legend>
        </>
      )}
    </Panel>
  )
}

const StatTable = ({ rows, columns, nameLabel, rowKey, sort, clickable, emptyText, onRow }) => {
  if (!rows.length) return <Empty>{emptyText}</Empty>
  const count = columns.length
  return (
    <Table>
      <THead $cols={count}>
        <THName type='button' onClick={() => sort.toggle('name')}>{nameLabel}{sort.arrow('name')}</THName>
        <THBar title='How this row splits across upcoming, due soon, overdue, on hold and completed work'>Mix</THBar>
        {columns.map(c => (
          <TH key={c.key} type='button' title={c.title} $hideMobile={!c.mobile} onClick={() => sort.toggle(c.key)}>{c.label}{sort.arrow(c.key)}</TH>
        ))}
      </THead>
      <TBody>
        {rows.map(r => (
          <TRow key={rowKey(r)} $cols={count}>
            <TDName title={`${r.name}: view their tasks`} $clickable={clickable} onClick={() => onRow(r)}>{r.name}</TDName>
            <TDBar title={bucketsFromRow(r).map(b => `${b.label}: ${b.count}`).join(' · ')}>
              <MiniBar>
                {bucketsFromRow(r).map(b => (
                  <MiniSeg
                    key={b.key}
                    style={{ width: `${(b.count / (bucketTotal(r) || 1)) * 100}%`, background: b.tint }}
                  />
                ))}
              </MiniBar>
            </TDBar>
            {columns.map(c => {
              const v = r[c.key]
              return (
                <TD
                  key={c.key}
                  $c={c.colour ? c.colour(v) : undefined}
                  $hideMobile={!c.mobile}
                  $clickable={clickable && !!c.drill}
                  onClick={c.drill ? () => onRow(r, c) : undefined}
                  title={c.title}
                >
                  {v == null ? '-' : c.percent ? `${v}%` : v}
                </TD>
              )
            })}
          </TRow>
        ))}
      </TBody>
    </Table>
  )
}

export default TaskAnalyticsPanel

const pulse = keyframes`0%,100%{opacity:.55}50%{opacity:.9}`

const MOBILE = '768px'

const Panel = styled.section`
  background: #fff; border: 1px solid #ececf1; border-radius: 14px;
  padding: 18px 20px 20px; margin-bottom: 22px;

  @media (max-width: ${MOBILE}) { padding: 14px 12px 16px; margin-bottom: 16px; }

  ${({ $embedded }) => $embedded && `
    border: none; border-radius: 0; padding: 0; margin: 0;
    @media (max-width: ${MOBILE}) { padding: 0; margin: 0; }
  `}
`

const Head = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; flex-wrap: wrap;
  margin-bottom: 16px;

  @media (max-width: ${MOBILE}) { gap: 10px; margin-bottom: 12px; }
`

const Title = styled.h3` margin: 0 0 2px; font-size: 16px; font-weight: 800; color: #101828; `
const Subtitle = styled.p`
  margin: 0; font-size: 12.5px; line-height: 1.45; color: #667085; max-width: 520px;

  @media (max-width: ${MOBILE}) { font-size: 12px; }
`

const HeadActions = styled.div`
  display: inline-flex; align-items: center; gap: 8px; flex-shrink: 0; flex-wrap: wrap; justify-content: flex-end;

  @media (max-width: ${MOBILE}) { width: 100%; justify-content: flex-start; gap: 8px; }
`

const Tiles = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px;
  transition: opacity 160ms ease; opacity: ${({ $dim }) => ($dim ? 0.55 : 1)};

  /* One number per full-width row wastes most of a phone screen. */
  @media (max-width: ${MOBILE}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
`
const Tile = styled.div`
  border: 1px solid #eceef3; border-radius: 12px; padding: 12px 14px;
  display: flex; flex-direction: column; gap: 4px; background: #fcfcfe;
  min-width: 0;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: border-color 120ms ease, box-shadow 120ms ease, background 120ms ease;
  ${({ $clickable }) => $clickable && `
    &:hover { border-color: #33becc; background: #f4fcfd; box-shadow: 0 2px 8px rgba(51,190,204,0.12); }
  `}
  @media (max-width: ${MOBILE}) { padding: 10px; gap: 2px; }
`
const TileIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 8px; margin-bottom: 2px;
  color: ${({ $c }) => $c}; background: ${({ $c }) => `${$c}14`};

  @media (max-width: ${MOBILE}) {
    width: 24px; height: 24px; border-radius: 7px; margin-bottom: 0;
    svg { font-size: 15px !important; }
  }
`
const TileVal = styled.div`
  font-size: 22px; font-weight: 800; line-height: 1.1; color: ${({ $c }) => $c || '#101828'};

  @media (max-width: ${MOBILE}) { font-size: 19px; }
`
const TileLbl = styled.div`
  font-size: 11.5px; font-weight: 600; color: #667085;

  @media (max-width: ${MOBILE}) { font-size: 11px; line-height: 1.25; }
`
const TileSkel = styled.div`
  height: 84px; border-radius: 12px; background: #eef0f5; animation: ${pulse} 1.3s ease-in-out infinite;
`

const Grids = styled.div`
  display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); gap: 16px; margin-top: 18px;
  transition: opacity 160ms ease; opacity: ${({ $dim }) => ($dim ? 0.55 : 1)};
  @media (max-width: 860px) { grid-template-columns: minmax(0, 1fr); }
  @media (max-width: ${MOBILE}) { gap: 12px; margin-top: 14px; }
`
const Block = styled.div`
  border: 1px solid #eceef3; border-radius: 12px; padding: 12px 14px 6px; min-width: 0;
  ${({ $wide }) => $wide && 'margin-top: 18px;'}

  transition: opacity 160ms ease; opacity: ${({ $dim }) => ($dim ? 0.55 : 1)};

  @media (max-width: ${MOBILE}) { padding: 10px 10px 4px; }
`
const BlockHead = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 8px;
  flex-wrap: wrap; min-width: 0;
`
const BlockTitle = styled.div` font-size: 12px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; color: #475467; display: inline-flex; align-items: center; gap: 6px; `
const BlockNote = styled.div` font-size: 12px; color: #667085; font-weight: 500; text-transform: none; letter-spacing: 0; margin-top: 3px; line-height: 1.45; `
const THBar = styled.div`
  font-size: 10.5px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase;
  color: #98a2b3; cursor: help; white-space: nowrap;
  @media (max-width: ${MOBILE}) { display: none; }
`
const TDBar = styled.div`
  display: flex; align-items: center; cursor: help; min-width: 0;
  @media (max-width: ${MOBILE}) { order: 1; grid-column: 1 / -1; margin: 6px 0 2px; }
`
const MiniBar = styled.div` display: flex; width: 100%; height: 8px; border-radius: 4px; overflow: hidden; background: #f1f5f9; `
const MiniSeg = styled.div` height: 100%; min-width: 2px; `
const SearchBox = styled.div`
  display: inline-flex; align-items: center; gap: 5px; padding: 4px 9px; border-radius: 8px;
  min-width: 0; max-width: 100%;
  background: #f6f7fb; border: 1px solid #e6e8f0; color: #98a2b3;
  input { font: inherit; border: none; background: transparent; outline: none; font-size: 12px; width: 120px; min-width: 0; color: #101828; }

  @media (max-width: ${MOBILE}) {
    flex: 1 1 120px;
    input { width: 100%; }
  }
`
const Empty = styled.div` padding: 14px 4px 16px; font-size: 12.5px; color: #98a2b3; `

const ErrorState = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 16px; border: 1px solid #f3d6cf; background: #fdf3f0; border-radius: 12px;
  font-size: 13px; color: #8a3b28;
`
const RetryBtn = styled.button`
  font: inherit; cursor: pointer; font-size: 12.5px; font-weight: 700;
  padding: 5px 12px; border-radius: 8px; border: 1px solid #e0b3a8; background: #fff; color: #b23c1e;
  &:hover { background: #fff4f1; }
`

const ChartTableRule = styled.div` height: 1px; background: #eef0f4; margin: 14px 0 10px; `
const Table = styled.div` display: flex; flex-direction: column; `
const gridCols = cols => `minmax(0, 1.6fr) minmax(0, 1fr) repeat(${cols}, minmax(0, 0.7fr))`

const THead = styled.div`
  display: grid; grid-template-columns: ${({ $cols }) => gridCols($cols)};
  gap: 6px; align-items: center;
  padding: 4px 6px 8px; border-bottom: 1px solid #eef0f4;

  @media (max-width: ${MOBILE}) {
    grid-template-columns: minmax(0, 1.4fr) repeat(3, minmax(0, 0.8fr));
    gap: 4px;
  }
`
const TH = styled.button`
  font: inherit; text-align: right; border: none; background: none; cursor: pointer;
  min-width: 0; padding: 4px 0; min-height: 32px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-size: 10.5px; font-weight: 800; letter-spacing: 0.03em; text-transform: uppercase; color: #667085;
  &:hover { color: #344054; }

  @media (max-width: ${MOBILE}) {
    font-size: 10px;
    ${({ $hideMobile }) => $hideMobile && 'display: none;'}
  }
`
const THName = styled(TH)` text-align: left; `
const TBody = styled.div` display: flex; flex-direction: column; max-height: 360px; overflow-y: auto; `
const TRow = styled.div`
  display: grid; grid-template-columns: ${({ $cols }) => gridCols($cols)};
  gap: 6px; align-items: center;
  padding: 7px 6px; border-bottom: 1px solid #f4f5f8;
  &:last-child { border-bottom: none; }

  @media (max-width: ${MOBILE}) {
    grid-template-columns: minmax(0, 1.4fr) repeat(3, minmax(0, 0.8fr));
    gap: 4px;
  }
`
const TDName = styled.div`
  min-width: 0;
  font-size: 13px; font-weight: 700; color: #101828;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  ${({ $clickable }) => $clickable && `&:hover { color: #2aa6b3; text-decoration: underline; }`}
`

const TD = styled.div`
  min-width: 0; text-align: right; font-size: 13px; font-weight: 700;
  color: ${({ $c }) => $c || '#475467'};

  @media (max-width: ${MOBILE}) {
    font-size: 12.5px;
    ${({ $hideMobile }) => $hideMobile && 'display: none;'}
  }

  border-radius: 6px; padding: 2px 4px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  ${({ $clickable }) => $clickable && `&:hover { background: #e6f7f9; }`}
`

const DomainList = styled.div` display: flex; flex-direction: column; `
const DomainRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 9px 6px; border-bottom: 1px solid #f4f5f8;
  &:last-child { border-bottom: none; }
`
const DomainName = styled.div`
  display: flex; align-items: center; gap: 8px; min-width: 0;
  font-size: 12.5px; font-weight: 700; color: #101828;
  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  ${({ $clickable }) => $clickable && `&:hover span { color: #2aa6b3; text-decoration: underline; }`}
`
const Dot = styled.span` flex: 0 0 auto; width: 10px; height: 10px; border-radius: 50%; background: ${({ $c }) => $c}; `
const DomainStats = styled.div`
  display: flex; align-items: center; gap: 8px; flex-shrink: 0; flex-wrap: wrap; justify-content: flex-end;

  @media (max-width: ${MOBILE}) { gap: 4px 8px; }
`
const DStat = styled.span`
  font-size: 11.5px; font-weight: 700; color: ${({ $c }) => $c || '#667085'};
  border-radius: 6px; padding: 1px 5px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  ${({ $clickable }) => $clickable && `&:hover { background: #eef4f5; text-decoration: underline; }`}
`
const SkelRows = styled.div` display: flex; flex-direction: column; gap: 8px; margin-top: 18px; `
const SkelRow = styled.div` height: 34px; border-radius: 8px; background: #eef0f5; animation: ${pulse} 1.3s ease-in-out infinite; `

const FilterRow = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
`
const PeriodNote = styled.p`
  margin: 8px 0 14px; font-size: 12.5px; color: #475467;
`
const OriginCards = styled.div`
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 12px;
  transition: opacity 160ms ease; opacity: ${({ $dim }) => ($dim ? 0.55 : 1)};

  @media (max-width: 860px) { grid-template-columns: minmax(0, 1fr); gap: 8px; }
`
const OriginCard = styled.button`
  font: inherit; text-align: left; cursor: pointer;
  display: flex; flex-direction: column; gap: 4px; min-width: 0;
  padding: 12px 14px; border-radius: 12px;
  border: 1px solid ${({ $active, $color }) => ($active ? $color : '#eceef3')};
  background: ${({ $active }) => ($active ? '#fff' : '#fcfcfe')};
  box-shadow: ${({ $active, $color }) => ($active ? `inset 0 0 0 1px ${$color}` : 'none')};
  &:hover { border-color: ${({ $color }) => $color}; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`
const OriginName = styled.span`
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 13.5px; font-weight: 800; color: #101828;
`
const OriginDesc = styled.span` font-size: 12px; color: #667085; line-height: 1.35; `
const OriginStats = styled.span`
  display: flex; flex-wrap: wrap; gap: 4px 12px; margin-top: 2px;
  font-size: 12.5px; color: #475467;
  strong { font-size: 14px; }
`
const Legend = styled.p`
  margin: 14px 0 0; font-size: 12px; line-height: 1.5; color: #667085;
  strong { color: #344054; }
`
